import { api } from './api';
import { Group, GroupAPI } from './groups';

export interface RecruiteeIntegrationAPI {
    connected: boolean;
    key_hint?: string;
    created_at?: string;
    created_by?: { id: string; name?: string };
    last_used_at?: string;
    recruitee_company_ids: string[];
}

export interface RecruiteeIntegration {
    connected: boolean;
    keyHint?: string;
    createdAt?: string;
    createdBy?: { id: string; name?: string };
    lastUsedAt?: string;
    recruiteeCompanyIds: string[];
}

export interface RecruiteeKeyAPI {
    key: string;
    key_hint: string;
    created_at: string;
}

export interface RecruiteeKey {
    key: string;
    keyHint: string;
    createdAt: string;
}

export const integrations = api.injectEndpoints({
    endpoints: (builder) => ({
        getRecruiteeIntegration: builder.query<RecruiteeIntegration, string>({
            query: (companyIdentifier) =>
                `companies/${companyIdentifier}/integrations/recruitee`,
            transformResponse: (
                response: RecruiteeIntegrationAPI,
            ): RecruiteeIntegration => ({
                connected: response.connected,
                keyHint: response.key_hint,
                createdAt: response.created_at,
                createdBy: response.created_by,
                lastUsedAt: response.last_used_at,
                recruiteeCompanyIds: response.recruitee_company_ids ?? [],
            }),
            providesTags: (result, error, companyIdentifier) => [
                { type: 'RecruiteeIntegration', id: companyIdentifier },
            ],
        }),

        // Generates the company's key, or regenerates it (which revokes the
        // old one). The response is the only time the plaintext key is
        // returned, so callers must reset() the mutation once it is shown.
        generateRecruiteeKey: builder.mutation<RecruiteeKey, string>({
            query: (companyIdentifier) => ({
                url: `companies/${companyIdentifier}/integrations/recruitee/key`,
                method: 'POST',
            }),
            transformResponse: (response: RecruiteeKeyAPI): RecruiteeKey => ({
                key: response.key,
                keyHint: response.key_hint,
                createdAt: response.created_at,
            }),
            invalidatesTags: (result, error, companyIdentifier) => [
                { type: 'RecruiteeIntegration', id: companyIdentifier },
            ],
        }),

        revokeRecruiteeKey: builder.mutation<{ connected: false }, string>({
            query: (companyIdentifier) => ({
                url: `companies/${companyIdentifier}/integrations/recruitee/key`,
                method: 'DELETE',
            }),
            invalidatesTags: (result, error, companyIdentifier) => [
                { type: 'RecruiteeIntegration', id: companyIdentifier },
            ],
        }),

        setGroupRecruitee: builder.mutation<
            Group,
            { companyIdentifier: string; groupId: string; isActive: boolean }
        >({
            query: ({ companyIdentifier, groupId, isActive }) => ({
                url: `companies/${companyIdentifier}/groups/${groupId}/integrations/recruitee`,
                method: 'PUT',
                body: {
                    is_active: isActive,
                },
            }),
            invalidatesTags: (result, error, { groupId }) => [
                {
                    type: 'Group',
                    groupId,
                },
            ],
            transformResponse: (response: GroupAPI) => ({
                ...response,
                type: response.type ?? 'CANDIDATE',
            }),
        }),
    }),
});

export const {
    useGetRecruiteeIntegrationQuery,
    useGenerateRecruiteeKeyMutation,
    useRevokeRecruiteeKeyMutation,
    useSetGroupRecruiteeMutation,
} = integrations;
